// src/pages/ExerciseListPage/index.js
// 仕様：
// - 指定レベル/章の問題一覧を表示
// - 進捗(progress)と突き合わせて「完成/未完成」をタグ表示（学生は自分のみ、教員/管理者は全体）
// - UI文言・コメントは日本語

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { List, Typography, Tag, Button, Breadcrumb, Spin } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { getChaptersByLevel, /* ← getExercisesByChapter 削除 */ getProgressForChapter } from '../../services/exerciseService';
import { useAuth } from '../../context/AuthContext';

const { Title } = Typography;

const ExerciseListPage = () => {
  const { levelId, chapterId } = useParams();
  const { userRole, currentUser } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [chapterTitle, setChapterTitle] = useState('');
  const [loading, setLoading] = useState(true);

  // 役割判定（学生）
  const isStudent =
    userRole?.name === '学生' ||
    userRole?.name === 'Học sinh' ||
    userRole?.name === '留学生' ||
    userRole?.name === 'Du học sinh';

  // データ取得
  const fetchData = useCallback(async () => {
    setLoading(true);

    // 1) 章のメタ情報と元の問題一覧を取得
    const chapterData = getChaptersByLevel(levelId)?.find((c) => c.id === chapterId);
    if (!chapterData) {
      setLoading(false);
      return;
    }

    setChapterTitle(chapterData.title);
    const baseExercises = chapterData.exercises || [];

    // 2) 進捗データ（progress）取得
    let progressList = [];
    try {
      const allProgress = await getProgressForChapter(levelId, chapterId);
      progressList = isStudent && currentUser
        ? allProgress.filter((p) => p.userId === (currentUser.id || currentUser.uid))
        : allProgress;
    } catch (e) {
      // 進捗が取得できなくても一覧は表示する
      progressList = [];
    }

    // 3) 進捗を高速参照するための Map（exerciseId -> status）
    const progressMap = new Map(progressList.map((p) => [p.exerciseId, p.status]));

    // 4) 元データにステータスを合成（completed -> 完成、それ以外 -> 未完成）
    const mergedExercises = baseExercises.map((ex) => ({
      ...ex,
      status: progressMap.get(ex.id) === 'completed' ? '完成' : '未完成',
    }));

    setExercises(mergedExercises);
    setLoading(false);
  }, [levelId, chapterId, isStudent, currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: 50 }} />;
  }

  return (
    <div>
      {/* パンくず */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Link to="/"><HomeOutlined /></Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/level/${levelId}`}>Level {levelId.replace('level', '')}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{chapterTitle}</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2}>問題リスト</Title>

      <List
        itemLayout="horizontal"
        dataSource={exercises}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Link
                to={`/level/${levelId}/chapter/${chapterId}/exercise/${item.id}`}
                key={item.id}
              >
                <Button type="primary">スタート</Button>
              </Link>,
            ]}
          >
            <List.Item.Meta
              title={<strong>{item.title}</strong>}
              description={`問題ID: ${item.id}`}
            />
            <Tag color={item.status === '完成' ? 'green' : 'volcano'}>
              {item.status}
            </Tag>
          </List.Item>
        )}
      />
    </div>
  );
};

export default ExerciseListPage;
